import { useState, useEffect, type ReactNode, type CSSProperties } from 'react';

interface DisclosureRowProps {
  label: ReactNode;
  /** Controlled-mode override. When undefined, the row owns its own state. */
  expanded?: boolean;
  onToggle?: (next: boolean) => void;
  defaultExpanded?: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Label color. Defaults to ink-3. */
  labelColor?: string;
  labelFontSize?: number;
}

/**
 * Phase 25 — collapsible row with unicode caret + 150ms ease-out animation.
 *
 * Pattern is repeated across the Scenario Diff page (top drivers,
 * year-by-year cumulative costs, per-scenario line-item lists). A native
 * <details>/<summary> would also work, but this gives finer control over
 * the caret rotation animation and the inline label styling.
 */
export default function DisclosureRow({
  label,
  expanded: controlledExpanded,
  onToggle,
  defaultExpanded = false,
  children,
  className,
  style,
  labelColor = 'var(--tco-ink-3)',
  labelFontSize = 12,
}: DisclosureRowProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? !!controlledExpanded : internalExpanded;

  useEffect(() => {
    if (isControlled) return;
    setInternalExpanded(defaultExpanded);
  }, [defaultExpanded, isControlled]);

  const handleToggle = () => {
    const next = !expanded;
    if (!isControlled) setInternalExpanded(next);
    onToggle?.(next);
  };

  return (
    <div className={className} style={style}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          fontFamily: 'inherit',
          fontSize: labelFontSize,
          color: labelColor,
          cursor: 'pointer',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        <span
          className="tco-caret"
          style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
          aria-hidden
        >
          ▶
        </span>
        <span>{label}</span>
      </button>
      {expanded && children && (
        <div className="tco-disclosure-content" style={{ marginTop: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
}
